import React, { FC } from 'react';
import { Circle as SvgCircle, G, Rect } from 'react-native-svg';
import {
  HANDLE_FILL_COLOR,
  SELECTION_BOX_DASH_PATTERN,
  SELECTION_BOX_OPACITY,
  SELECTION_BOX_PADDING,
  SELECTION_STROKE_COLOR,
} from '../../constants';
import { Annotation, AnnotationType, DocumentRotation, TextAnnotation } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { useManageSelectionHandles } from './hooks/useManageSelectionHandles';

interface SelectionHandlesProps {
  annotation: Annotation;
  /** Current image zoom — passed through so handles stay a constant on-screen size. */
  zoom: number;
  /**
   * Document rotation in degrees. Text annotations have their glyphs rotated around the position
   * anchor by this angle (see `AnnotationShape.renderText`); the selection box + handles need to
   * spin around the same anchor so they stay aligned with the rotated text.
   */
  documentRotation?: DocumentRotation;
}

const SelectionHandles: FC<SelectionHandlesProps> = ({ annotation, zoom, documentRotation = 0 }) => {
  const { handles, bbox, handleRadius, handleStrokeWidth } = useManageSelectionHandles({ annotation, zoom });

  const groupTransform =
    documentRotation && annotation.type === AnnotationType.Text
      ? `rotate(${documentRotation} ${(annotation as TextAnnotation).position.x} ${(annotation as TextAnnotation).position.y})`
      : undefined;

  return (
    <G transform={groupTransform}>
      {bbox ? (
        <Rect
          x={bbox.x - SELECTION_BOX_PADDING}
          y={bbox.y - SELECTION_BOX_PADDING}
          width={bbox.width + SELECTION_BOX_PADDING * 2}
          height={bbox.height + SELECTION_BOX_PADDING * 2}
          fill="none"
          stroke={SELECTION_STROKE_COLOR}
          strokeWidth={1}
          strokeDasharray={SELECTION_BOX_DASH_PATTERN}
          opacity={SELECTION_BOX_OPACITY}
        />
      ) : null}
      {handles.map((h) => (
        <SvgCircle
          key={h.id}
          cx={h.x}
          cy={h.y}
          r={handleRadius}
          fill={HANDLE_FILL_COLOR}
          stroke={SELECTION_STROKE_COLOR}
          strokeWidth={handleStrokeWidth}
        />
      ))}
    </G>
  );
};

export default SelectionHandles;
